import * as React from 'react'
import { ModuleContext } from '../hooks/ModuleContext'
import { SelfServiceContainer } from '@/SelfServiceContainer'
import { GeneralModule } from '@/modules/GeneralModule'
import { ReturnsAndExchangesModule } from '@/modules/ReturnsAndExchangesModule'
import { WhatsappModule } from '@/modules/WhatsappModule'
import { FAQModule } from '@/modules/FAQModule'
import { ChatModule } from '@/modules/ChatModule'
import { FAQModuleProps } from '@/modules/FAQModule/types'
import { WhatsappModuleProps } from '@/modules/WhatsappModule/types'
import { ReturnsAndExchangesModuleProps } from '@/modules/ReturnsAndExchangesModule/types'
import { OrdersModule } from '@/modules/OrdersModule'
import { OrderProps, OrdersModuleProps } from '@/modules/OrdersModule/types'
import { getAllSectionsBase, getSection } from '@/requests/webSelfService'
import { getOrders } from '@/requests/order'
import { getAccessToken } from '@/services/cookieDealer'
import { ScheduleModule } from '@/modules/ScheduleModule'
import { getSchedules } from '@/requests/schedule'
import { ScheduleProps } from '@/modules/ScheduleModule/types'

type BaseDataType = {
  id: string
  icon: string
  title: string
  isAvailable?: boolean
  isAvailableText?: string
  notAvailableText?: string
}

type Module = 'all' | 'returns-exchanges' | 'whatsapp' | 'faq' | 'chat' | 'orders' | 'schedules'

type ModuleDataType = ReturnsAndExchangesModuleProps | WhatsappModuleProps | FAQModuleProps | OrdersModuleProps | null

export const ModuleController = (props: { apiUrl: string; userId: string }) => {
  const [baseData, setBaseData] = React.useState<BaseDataType[] | undefined>(undefined)
  const [moduleData, setModuleData] = React.useState<ModuleDataType>(null)
  const [whatsappIsOff, setWhatsappIsOff] = React.useState<boolean>(true)
  const [isLogged, setIsLogged] = React.useState<boolean>(false)
  const [ordersError, setOrdersError] = React.useState<boolean>(false)
  const [ordersLoaded, setOrdersLoaded] = React.useState<boolean>(false)
  const [allOrders, setAllOrders] = React.useState<OrderProps[] | undefined>(undefined)
  const [schedulesError, setSchedulesError] = React.useState<boolean>(false)
  const [schedulesLoaded, setSchedulesLoaded] = React.useState<boolean>(false)
  const [allSchedules, setAllSchedules] = React.useState<ScheduleProps[] | undefined>(undefined)
  const { module } = React.useContext(ModuleContext)

  React.useEffect(() => {
    const baseFetch = async (apiUrl: string) => {
      const data = await getAllSectionsBase(apiUrl)
      setWhatsappIsOff(!data?.some((module: BaseDataType) => module.id == 'whatsapp'))
      setWhatsappIsOff(!data?.some((module: BaseDataType) => module.id == 'whatsapp'))
      setBaseData([
        { id: 'orders', icon: 'truck', title: 'Acompanhar pedido', isAvailable: true },
        { id: 'schedules', icon: 'petz-calendar', title: 'Consultar agendamentos', isAvailable: true },
        ...data,
      ])
    }

    baseFetch(props.apiUrl)
  }, [])

  React.useEffect(() => {
    const fetchSection = async (apiUrl: string, userId: string) => {
      try {
        if (module == 'orders') {
          if (!getAccessToken() || userId == '') {
            setOrdersError(false)
            setIsLogged(false)
            setOrdersLoaded(true)
            return
          }
          const result = await getOrders(apiUrl, userId)

          if (result === null) {
            setOrdersError(true)
            setIsLogged(true)
            return
          }

          setOrdersError(false)
          setIsLogged(true)
          setAllOrders(result.ordersInTransit)
          setOrdersLoaded(true)
        } else if (module == 'schedules') {
          if (!getAccessToken() || userId == '') {
            setSchedulesError(false)
            setIsLogged(false)
            setSchedulesLoaded(true)
            return
          }
          const result = await getSchedules(apiUrl)

          if (result === null) {
            setSchedulesError(true)
            setIsLogged(true)
            return
          }

          setSchedulesError(false)
          setIsLogged(true)
          setAllSchedules(result)
          setSchedulesLoaded(true)
        } else {
          const data = await getSection(apiUrl, module)
          setModuleData(data)
        }
      } catch (error) {
        console.error('Error:', error)
      }
    }

    if (module !== 'all' && module !== 'chat') {
      fetchSection(props.apiUrl, props.userId)
    } else {
      setModuleData(null)
    }
  }, [module, props.userId])

  const generalModuleTitle = 'Como podemos ajudar?'

  const title = baseData ? (baseData.find((content) => content.id === module)?.title ?? generalModuleTitle) : generalModuleTitle

  const moduleContent: Record<Module, React.JSX.Element | null> = {
    'all': baseData ? <GeneralModule sections={baseData} /> : null,
    'orders': <OrdersModule isLogged={isLogged} orders={allOrders} loaded={ordersLoaded} error={ordersError} />,
    'schedules': <ScheduleModule isLogged={isLogged} schedules={allSchedules} error={schedulesError} loaded={schedulesLoaded} />,
    'returns-exchanges': moduleData ? <ReturnsAndExchangesModule {...(moduleData as ReturnsAndExchangesModuleProps)} /> : null,
    'whatsapp': moduleData ? <WhatsappModule {...(moduleData as WhatsappModuleProps)} /> : null,
    'faq': moduleData ? <FAQModule {...(moduleData as FAQModuleProps)} /> : null,
    'chat': <ChatModule />,
  }

  return baseData ? (
    <SelfServiceContainer whatsappIsOff={whatsappIsOff} generalModule={module === 'all'} title={module === 'all' ? generalModuleTitle : title}>
      {moduleContent[module as Module] || moduleContent['all']}
    </SelfServiceContainer>
  ) : null
}
